import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Skeleton,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    DeleteOutline as DeleteIcon,
    Store as StoreIcon,
} from '@mui/icons-material';

import {
    fetchCart,
    updateCartItem,
    removeCartItem,
    dismissCartNotice,
} from '../../actions/cartActions';
import { formatPrice } from '../../lib/money';
import ProductImage from '../storefront/ProductImage';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    callButtonSx,
    secondaryButtonSx,
    skeletonSx,
} from '../storefront/storefrontStyles';
import CartLayout, { darkAlertSx, panelSx } from './CartLayout';
import QuantityStepper from './QuantityStepper';
import {
    BLOCKED_REASON_LABELS,
    UNAVAILABLE_LABELS,
    checkoutPath,
} from './cartPaths';

const storePath = (storeId) => `/tienda/${storeId}`;

// Colores del texto de una línea según el fondo donde se dibuja. 'dark' es
// el storefront (/carrito, checkout); 'light' es un Dialog de MUI (el carrito
// en la llamada y el vivo), donde el blanco de la paleta se perdía.
const LINE_TONES = {
    dark: {
        text: COLORS.text,
        muted: COLORS.muted,
        subtle: COLORS.subtle,
    },
    light: {
        text: 'rgba(0,0,0,0.87)',
        muted: 'rgba(0,0,0,0.6)',
        subtle: 'rgba(0,0,0,0.6)',
    },
};

export const CartLine = ({
    item,
    pending,
    onQuantity,
    onRemove,
    readOnly,
    tone = 'dark',
}) => {
    const unavailable = !item.available;
    const colors = LINE_TONES[tone] || LINE_TONES.dark;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                py: 2,
                alignItems: 'flex-start',
                ...(unavailable
                    ? {
                          px: 1.5,
                          mx: -1.5,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(226,75,74,0.08)',
                          border: '1px solid rgba(226,75,74,0.35)',
                      }
                    : {}),
            }}
        >
            <Box
                sx={{
                    width: { xs: 64, md: 80 },
                    flexShrink: 0,
                    opacity: unavailable ? 0.5 : 1,
                }}
            >
                <ProductImage
                    src={item.imageUrl}
                    alt={item.name}
                    sx={{ borderRadius: '8px' }}
                />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        textDecoration: unavailable ? 'line-through' : 'none',
                        color: unavailable ? colors.muted : colors.text,
                    }}
                >
                    {item.name}
                </Typography>
                {item.variantLabel && (
                    <Typography
                        sx={{
                            color: colors.muted,
                            fontFamily: FONT_BODY,
                            fontSize: '0.85rem',
                        }}
                    >
                        {item.variantLabel}
                    </Typography>
                )}
                {unavailable ? (
                    <Typography
                        sx={{
                            color: COLORS.closed,
                            fontFamily: FONT_BODY,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            mt: 0.5,
                        }}
                    >
                        {UNAVAILABLE_LABELS[item.unavailableReason] ||
                            UNAVAILABLE_LABELS.product_unavailable}
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            color: colors.subtle,
                            fontFamily: FONT_BODY,
                            fontSize: '0.85rem',
                            mt: 0.25,
                        }}
                    >
                        {formatPrice(item.unitPrice)} c/u
                        {readOnly ? ` · ${item.quantity} u.` : ''}
                    </Typography>
                )}
                {!readOnly && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                        }}
                    >
                        {!unavailable && (
                            <QuantityStepper
                                size="small"
                                tone={tone}
                                value={item.quantity}
                                disabled={pending}
                                onChange={(quantity) =>
                                    onQuantity(item, quantity)
                                }
                            />
                        )}
                        <Tooltip title="Quitar del carrito">
                            <span>
                                <IconButton
                                    aria-label={`Quitar ${item.name}`}
                                    disabled={pending}
                                    onClick={() => onRemove(item)}
                                    sx={{
                                        color: colors.muted,
                                        '&:hover': { color: COLORS.closed },
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {pending && (
                            <CircularProgress
                                size={16}
                                sx={{ color: colors.muted }}
                            />
                        )}
                    </Box>
                )}
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: unavailable ? colors.subtle : colors.text,
                }}
            >
                {unavailable ? '—' : formatPrice(item.lineTotal)}
            </Typography>
        </Box>
    );
};

export const CartStoreHeader = ({ store }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Avatar
            src={(store && store.profileImageUrl) || undefined}
            alt={store ? store.name : ''}
            sx={{
                width: 40,
                height: 40,
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
            }}
        >
            <StoreIcon sx={{ color: '#555' }} />
        </Avatar>
        <Typography
            sx={{
                fontFamily: FONT_TITLE,
                fontWeight: 700,
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
            }}
        >
            {store ? store.name : 'Tienda'}
        </Typography>
    </Box>
);

const CartSkeleton = () => (
    <Box sx={panelSx}>
        {[0, 1].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, py: 2 }}>
                <Skeleton
                    variant="rectangular"
                    width={72}
                    height={72}
                    sx={{ ...skeletonSx, borderRadius: '8px' }}
                />
                <Box sx={{ flex: 1 }}>
                    <Skeleton sx={skeletonSx} width="60%" />
                    <Skeleton sx={skeletonSx} width="30%" />
                </Box>
            </Box>
        ))}
    </Box>
);

const CartPage = ({
    storeId,
    storeCart,
    fetchCart,
    updateCartItem,
    removeCartItem,
    dismissCartNotice,
}) => {
    const navigate = useNavigate();

    useEffect(() => {
        fetchCart(storeId);
    }, [storeId, fetchCart]);

    const cart = storeCart && storeCart.cart;
    const pendingItems = (storeCart && storeCart.pendingItems) || {};
    const notice = storeCart && storeCart.notice;
    const storeName = cart && cart.store ? cart.store.name : null;
    const backLabel = storeName
        ? `Volver a ${storeName}`
        : 'Volver a la tienda';

    let content;
    if (!cart) {
        content =
            storeCart && storeCart.failed ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography sx={{ color: COLORS.muted, mb: 2 }}>
                        No pudimos cargar tu carrito.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => fetchCart(storeId)}
                        sx={secondaryButtonSx}
                    >
                        Reintentar
                    </Button>
                </Box>
            ) : (
                <CartSkeleton />
            );
    } else if (cart.items.length === 0) {
        content = (
            <Box sx={{ ...panelSx, textAlign: 'center', py: 8 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        textTransform: 'uppercase',
                        mb: 1,
                    }}
                >
                    Tu carrito está vacío
                </Typography>
                <Typography sx={{ color: COLORS.muted, mb: 3 }}>
                    Mirá el catálogo de {storeName || 'la tienda'} y agregá lo
                    que te guste.
                </Typography>
                <Button
                    variant="outlined"
                    component={RouterLink}
                    to={storePath(storeId)}
                    sx={{ ...secondaryButtonSx, px: 4 }}
                >
                    Ver la tienda
                </Button>
            </Box>
        );
    } else {
        const { checkout } = cart;
        const blockedReason = !checkout.canCheckout
            ? BLOCKED_REASON_LABELS[checkout.blockedReason] ||
              'No se puede continuar con esta compra.'
            : null;

        content = (
            <Box sx={panelSx}>
                <CartStoreHeader store={cart.store} />
                <Divider sx={{ borderColor: COLORS.border }} />
                {cart.items.map((item) => (
                    <Box key={item.cartItemId}>
                        <CartLine
                            item={item}
                            pending={!!pendingItems[item.cartItemId]}
                            onQuantity={(line, quantity) =>
                                updateCartItem(
                                    storeId,
                                    line.cartItemId,
                                    quantity
                                )
                            }
                            onRemove={(line) =>
                                removeCartItem(storeId, line.cartItemId)
                            }
                        />
                        <Divider sx={{ borderColor: COLORS.border }} />
                    </Box>
                ))}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mt: 2,
                    }}
                >
                    <Typography sx={{ color: COLORS.muted }}>
                        Subtotal ({cart.itemCount}{' '}
                        {cart.itemCount === 1 ? 'producto' : 'productos'})
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_BODY,
                            fontWeight: 700,
                            fontSize: '1.4rem',
                        }}
                    >
                        {formatPrice(cart.subtotal)}
                    </Typography>
                </Box>
                {cart.hasUnavailable && (
                    <Typography
                        sx={{
                            color: COLORS.subtle,
                            fontSize: '0.8rem',
                            textAlign: 'right',
                        }}
                    >
                        No incluye los productos no disponibles.
                    </Typography>
                )}
                {blockedReason && (
                    <Alert severity="warning" sx={{ ...darkAlertSx, mt: 2 }}>
                        {blockedReason}
                    </Alert>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        mt: 3,
                    }}
                >
                    <Button
                        variant="outlined"
                        component={RouterLink}
                        to={storePath(storeId)}
                        sx={secondaryButtonSx}
                    >
                        Seguir comprando
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!checkout.canCheckout}
                        onClick={() => navigate(checkoutPath(storeId))}
                        sx={{
                            ...callButtonSx,
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(255,255,255,0.12)',
                                color: 'rgba(255,255,255,0.4)',
                            },
                        }}
                    >
                        Continuar compra
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <CartLayout
            backTo={storePath(storeId)}
            backLabel={backLabel}
            title="Mi carrito"
        >
            {notice && (
                <Alert
                    severity="warning"
                    onClose={() => dismissCartNotice(storeId)}
                    sx={{ ...darkAlertSx, mb: 2 }}
                >
                    {notice}
                </Alert>
            )}
            {content}
        </CartLayout>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        storeCart: state.cart.byStore[ownProps.storeId],
    };
};

const ConnectedCartPage = connect(mapStateToProps, {
    fetchCart,
    updateCartItem,
    removeCartItem,
    dismissCartNotice,
})(CartPage);

const CartPageWrapper = () => {
    const { storeId } = useParams();
    return <ConnectedCartPage key={storeId} storeId={storeId} />;
};

export default CartPageWrapper;
